import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Chip,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/joy';
import { FaSearch, FaFileAlt, FaQuoteLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import { documentsService } from '../modules/docs/services/documents.service';
import { documentLinkingService } from '../services/documentLinkingService';
import { matchesLinkableSearch } from '../services/documentLinkingSearch';

interface DocumentLinkerProps {
  projectId: string;
  onLinkCreated?: () => void;
  hideHeader?: boolean;
}

export const DocumentLinker = forwardRef<{ openModal: () => void }, DocumentLinkerProps>(({ 
  projectId, 
  onLinkCreated,
  hideHeader = false
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Expose openModal function to parent component
  const openModal = () => {
    setIsSearching(true);
    setIsOpen(true);
  };
  
  // Make openModal available to parent via ref
  useImperativeHandle(ref, () => ({
    openModal
  }));

  type LinkerDoc = {
    id: string;
    document_ref: string;
    title: string;
    kind: 'Estimate' | 'Quotation';
    customer_first_name: string;
    customer_last_name: string;
    grand_total: number;
    status: string;
    created_at: string;
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [allDocuments, setAllDocuments] = useState<LinkerDoc[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setCurrentPage(1);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsSearching(true);
      setSearchError(null);
      try {
        const [linkableDocs, linkedDocs] = await Promise.all([
          documentLinkingService.getAllLinkableDocuments(),
          documentsService.getDocumentsByProject(projectId),
        ]);
        if (cancelled) return;
        const linkedIds = new Set(linkedDocs.map((doc) => doc.id));
        setAllDocuments(
          linkableDocs
            .filter((doc) => (doc.type === 'Estimate' || doc.type === 'Quotation') && !linkedIds.has(doc.id))
            .map((doc) => {
              const parts = (doc.customerName || '').split(' ').filter(Boolean);
              return {
                id: doc.id,
                document_ref: doc.reference || '',
                title: doc.title || '',
                kind: doc.type as 'Estimate' | 'Quotation',
                customer_first_name: parts[0] || '',
                customer_last_name: parts.slice(1).join(' '),
                grand_total: doc.amount || 0,
                status: doc.status || 'draft',
                created_at: doc.createdAt || new Date().toISOString(),
              };
            })
        );
      } catch (err) {
        console.error('Error searching documents:', err);
        if (!cancelled) setSearchError('An error occurred while searching for documents.');
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId]);

  const searchResults = useMemo(
    () =>
      allDocuments.filter((doc) =>
        matchesLinkableSearch(
          {
            reference: doc.document_ref,
            title: doc.title,
            customerFirstName: doc.customer_first_name,
            customerLastName: doc.customer_last_name,
            kind: doc.kind,
          },
          searchQuery
        )
      ),
    [allDocuments, searchQuery]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const totalPages = Math.max(1, Math.ceil(searchResults.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentResults = searchResults.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleCreateLink = async (reference: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Find JARVIS document by reference
      const document = allDocuments.find((doc) => doc.id === reference || doc.document_ref === reference);
      if (document && document.id) {
        await documentsService.updateDocument(document.id, { projectId });
      } else {
        throw new Error('Document not found');
      }
      
      setIsOpen(false);
      setSearchQuery('');
      setAllDocuments([]);
      onLinkCreated?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create link';
      setError(errorMessage);
      console.error('DocumentLinker - error creating link:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'sent': return 'primary';
      case 'paid': return 'success';
      case 'draft': return 'neutral';
      case 'rejected': return 'danger';
      default: return 'neutral';
    }
  };

  const getDocumentIcon = (kind: 'Estimate' | 'Quotation') => {
    return kind === 'Estimate' ? FaFileAlt : FaQuoteLeft;
  };

  return (
    <Box>
      {/* Error display */}
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search Modal */}
      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <ModalDialog 
          size="lg" 
          sx={{ 
            maxHeight: { xs: 'calc(90vh - 56px - env(safe-area-inset-top, 0px))', sm: '90vh' },
            width: { xs: '95vw', sm: '80vw', md: '70vw' },
            maxWidth: '800px',
            overflow: 'hidden', 
            display: 'flex', 
            flexDirection: 'column',
            m: { xs: 1, sm: 2 },
            // Account for mobile header
            mt: { xs: 'calc(56px + env(safe-area-inset-top, 0px) + 8px)', sm: 2 },
            // Ensure modal appears above header
            zIndex: 1400
          }}
        >
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Link Document
          </Typography>

          {/* Search Section */}
          <Box sx={{ mb: 2 }}>
            <Typography level="body-sm" sx={{ mb: 1 }}>
              Search quotes and estimates by reference, title, or customer
            </Typography>
            <Input
              placeholder="Try a name, title, or KSG reference…"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              startDecorator={isSearching ? <CircularProgress size="sm" /> : <IconWrapper icon={FaSearch} />}
              sx={{ mb: 2 }}
            />
          </Box>

          {searchError && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {searchError}
            </Alert>
          )}

          {/* Results Section */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Typography level="title-sm" sx={{ mb: 2 }}>
              Available Documents ({searchResults.length})
            </Typography>
            {isSearching && allDocuments.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {!isSearching && searchResults.length === 0 && (
              <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 2 }}>
                {searchQuery.trim()
                  ? `No quotes or estimates match “${searchQuery.trim()}”.`
                  : 'No unlinked quotes or estimates found.'}
              </Typography>
            )}
            <List sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
              {currentResults.map((document) => (
                <ListItem
                  key={document.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 'sm',
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'background.level1'
                    }
                  }}
                >
                  <ListItemDecorator>
                    <IconWrapper icon={getDocumentIcon(document.kind)} />
                  </ListItemDecorator>
                  
                  <ListItemContent>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', sm: 'flex-start' },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Document Reference - bold, top */}
                        <Typography level="title-sm" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          {document.document_ref || document.id}
                        </Typography>
                        
                        {/* Title */}
                        <Typography level="body-sm" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mt: 0.5 }}>
                          {document.title}
                        </Typography>
                        
                        {/* Customer name - below, smaller */}
                        <Typography level="body-sm" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: 'neutral.600' }}>
                          {document.customer_first_name && document.customer_last_name 
                            ? `${document.customer_first_name} ${document.customer_last_name}` 
                            : 'Unknown Customer'}
                        </Typography>
                        
                        {/* Amount and status - below */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 0.5, sm: 1 }, 
                          alignItems: { xs: 'flex-start', sm: 'center' }, 
                          mt: 0.5 
                        }}>
                          <Typography level="body-sm" fontWeight="medium" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            {formatCurrency(document.grand_total)}
                          </Typography>
                          <Chip 
                            size="sm" 
                            variant="soft" 
                            color={document.kind === 'Estimate' ? 'primary' : 'success'}
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {document.kind}
                          </Chip>
                          <Chip 
                            size="sm" 
                            variant="soft" 
                            color={getStatusColor(document.status)}
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {document.status}
                          </Chip>
                        </Box>
                      </Box>
                      
                      {/* Link Document button */}
                      <Button
                        size="sm"
                        onClick={() => handleCreateLink(document.id)}
                        disabled={loading}
                        sx={{ 
                          minWidth: { xs: '100%', sm: 'auto' },
                          mt: { xs: 1, sm: 0 }
                        }}
                      >
                        Link Document
                      </Button>
                    </Box>
                  </ListItemContent>
                </ListItem>
              ))}
            </List>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: 1,
                py: 1,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <IconButton
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <IconWrapper icon={FaChevronLeft} />
                </IconButton>
                
                <Typography level="body-sm">
                  Page {currentPage} of {totalPages}
                </Typography>
                
                <IconButton
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <IconWrapper icon={FaChevronRight} />
                </IconButton>
              </Box>
            )}
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
});

